import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  cancelPurchaseOrder,
  getPurchaseOrder,
  patchPurchaseOrder,
  receivePurchaseOrder,
  sendPurchaseOrder,
} from '@/lib/api/purchase-orders.js';
import { formatCurrency, formatDateTime, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { getCurrentStore } from '@/lib/api/stores.js';
import { printInventoryLabelsWithFallback } from '@/lib/print.js';
import { PrintInventoryLabelsModal } from '@/components/inventory/PrintInventoryLabelsModal.js';

function statusBadge(status: string): string {
  switch (status) {
    case 'draft':
      return 'bg-slate-100 text-slate-700';
    case 'sent':
      return 'bg-blue-100 text-blue-800';
    case 'partially_received':
      return 'bg-amber-100 text-amber-900';
    case 'received':
      return 'bg-green-100 text-green-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

function lineRowClass(qtyOrdered: number, qtyReceived: number): string {
  if (qtyReceived >= qtyOrdered) return 'bg-green-50';
  if (qtyReceived > 0) return 'bg-amber-50/80';
  return 'bg-white';
}

export function PurchaseOrderDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveDraft, setReceiveDraft] = useState<Record<string, { qty: string; cost: string }>>({});
  const [labelItems, setLabelItems] = useState<Array<{ itemId: string; name?: string; quantity?: number }>>(
    [],
  );
  const [printLabelsOpen, setPrintLabelsOpen] = useState(false);
  const [tracking, setTracking] = useState('');
  const [supplierRef, setSupplierRef] = useState('');
  const [poNotes, setPoNotes] = useState('');

  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });

  const poQuery = useQuery({
    queryKey: ['purchase-order', id],
    queryFn: () => getPurchaseOrder(id),
    enabled: Boolean(id),
  });

  const po = poQuery.data;

  useEffect(() => {
    if (!po?.lines) return;
    const next: Record<string, { qty: string; cost: string }> = {};
    for (const line of po.lines) {
      const out = Math.max(line.qtyOrdered - line.qtyReceived, 0);
      next[line.id] = {
        qty: out > 0 ? String(out) : '0',
        cost: line.unitCost,
      };
    }
    setReceiveDraft(next);
  }, [po?.id, po?.lines]);

  useEffect(() => {
    if (!po) return;
    setTracking(po.trackingNumber ?? '');
    setSupplierRef(po.supplierRef ?? '');
    setPoNotes(po.notes ?? '');
  }, [po?.id, po?.trackingNumber, po?.supplierRef, po?.notes]);

  const patchMut = useMutation({
    mutationFn: (body: Parameters<typeof patchPurchaseOrder>[1]) => patchPurchaseOrder(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-order', id] });
      toastSuccess('PO updated');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const sendMut = useMutation({
    mutationFn: () => sendPurchaseOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-order', id] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      toastSuccess('PO sent');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const cancelMut = useMutation({
    mutationFn: () => cancelPurchaseOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-order', id] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      toastSuccess('PO cancelled');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const receiveMut = useMutation({
    mutationFn: () => {
      if (!po) throw new Error('missing');
      const lines = po.lines
        .map((line) => {
          const d = receiveDraft[line.id];
          const qty = d ? Number(d.qty) : 0;
          if (qty <= 0) return null;
          return {
            poLineId: line.id,
            qtyReceived: qty,
            actualUnitCost: d?.cost ? Number(d.cost) : undefined,
            itemId: line.itemId,
            itemName: line.itemName,
          };
        })
        .filter(Boolean) as Array<{
        poLineId: string;
        qtyReceived: number;
        actualUnitCost?: number;
        itemId: string;
        itemName: string;
      }>;
      const payload = lines.map(({ poLineId, qtyReceived, actualUnitCost }) => ({
        poLineId,
        qtyReceived,
        actualUnitCost,
      }));
      return receivePurchaseOrder(id, { lines: payload }).then((received) => ({
        lines,
        fulfilled: received.partsFulfilled ?? [],
      }));
    },
    onSuccess: async ({ lines, fulfilled }) => {
      queryClient.invalidateQueries({ queryKey: ['purchase-order', id] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
      queryClient.invalidateQueries({ queryKey: ['parts-request-queue'] });
      setReceiveOpen(false);
      toastSuccess(
        fulfilled.length
          ? `Stock received. Fulfilled ${fulfilled.map((row) => row.ticketNumber).join(', ')}`
          : 'Stock received',
      );

      const labelRows = lines
        .filter((l) => l.itemId && l.qtyReceived > 0)
        .map((l) => ({
          itemId: l.itemId,
          name: l.itemName,
          quantity: l.qtyReceived,
        }));
      if (labelRows.length === 0) return;

      if (storeQuery.data?.autoLabelOnReceive) {
        await printInventoryLabelsWithFallback(
          labelRows.map((r) => ({ itemId: r.itemId, quantity: r.quantity })),
        );
      } else {
        setLabelItems(labelRows);
        setPrintLabelsOpen(true);
      }
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  if (poQuery.isLoading || !po) {
    return <div className="text-slate-600">Loading…</div>;
  }

  const canEditMeta = po.status === 'draft' || po.status === 'sent' || po.status === 'partially_received';

  function saveMeta(e: FormEvent) {
    e.preventDefault();
    patchMut.mutate({
      trackingNumber: tracking || null,
      supplierRef: supplierRef || null,
      notes: poNotes || null,
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/inventory/purchase-orders" className="text-sm font-semibold text-primary">
          ← Purchase orders
        </Link>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-950">{po.poNumber}</h1>
            <p className="text-slate-600">{po.supplierName}</p>
          </div>
          <span className={['inline-flex rounded-full px-3 py-1 text-xs font-bold', statusBadge(po.status)].join(' ')}>
            {prettyLabel(po.status)}
          </span>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {po.status === 'draft' ? (
            <>
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
                onClick={() => sendMut.mutate()}
                disabled={sendMut.isPending}
              >
                Send PO
              </button>
              <button
                type="button"
                className="rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-800"
                onClick={() => cancelMut.mutate()}
                disabled={cancelMut.isPending}
              >
                Cancel
              </button>
            </>
          ) : null}
          {po.status === 'sent' || po.status === 'partially_received' ? (
            <>
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
                onClick={() => setReceiveOpen(true)}
              >
                {po.status === 'sent' ? 'Receive stock' : 'Receive more'}
              </button>
              <button
                type="button"
                className="rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-800"
                onClick={() => cancelMut.mutate()}
                disabled={cancelMut.isPending}
              >
                Cancel
              </button>
            </>
          ) : null}
        </div>

        <form className="mt-6 grid gap-3 border-t border-slate-200 pt-4 md:grid-cols-2" onSubmit={saveMeta}>
          <div className="text-sm text-slate-600">
            Created: {formatDateTime(po.createdAt)}
            {po.expectedAt ? ` · Expected: ${formatDateTime(po.expectedAt)}` : null}
          </div>
          <label className="block text-sm md:col-span-2">
            <span className="font-medium text-slate-700">Tracking #</span>
            <input
              disabled={!canEditMeta}
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 disabled:bg-slate-100"
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
            />
          </label>
          <label className="block text-sm md:col-span-2">
            <span className="font-medium text-slate-700">Supplier ref #</span>
            <input
              disabled={!canEditMeta}
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 disabled:bg-slate-100"
              value={supplierRef}
              onChange={(e) => setSupplierRef(e.target.value)}
            />
          </label>
          <label className="block text-sm md:col-span-2">
            <span className="font-medium text-slate-700">Notes</span>
            <textarea
              disabled={!canEditMeta}
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 disabled:bg-slate-100"
              rows={2}
              value={poNotes}
              onChange={(e) => setPoNotes(e.target.value)}
            />
          </label>
          {canEditMeta ? (
            <button type="submit" className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold">
              Save info
            </button>
          ) : null}
        </form>

        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Item</th>
                <th className="px-4 py-2">Ordered</th>
                <th className="px-4 py-2">Received</th>
                <th className="px-4 py-2">Outstanding</th>
                <th className="px-4 py-2">Unit cost</th>
                <th className="px-4 py-2">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {po.lines.map((line) => {
                const out = Math.max(line.qtyOrdered - line.qtyReceived, 0);
                return (
                  <tr key={line.id} className={lineRowClass(line.qtyOrdered, line.qtyReceived)}>
                    <td className="px-4 py-2 font-medium text-slate-900">{line.itemName}</td>
                    <td className="px-4 py-2">{line.qtyOrdered}</td>
                    <td className="px-4 py-2">{line.qtyReceived}</td>
                    <td className="px-4 py-2">{out}</td>
                    <td className="px-4 py-2">{formatCurrency(line.unitCost)}</td>
                    <td className="px-4 py-2">{formatCurrency(line.lineTotal)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex justify-end gap-4 text-sm text-slate-700">
          <div>
            Subtotal: <span className="font-semibold">{formatCurrency(po.subtotal)}</span>
          </div>
          <div>
            Shipping: <span className="font-semibold">{formatCurrency(po.shippingCost)}</span>
          </div>
          <div>
            Total: <span className="font-bold text-slate-950">{formatCurrency(po.total)}</span>
          </div>
        </div>
      </div>

      {receiveOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-slate-950">Receive stock</h3>
            <div className="mt-4 space-y-3">
              {po.lines.map((line) => {
                const out = Math.max(line.qtyOrdered - line.qtyReceived, 0);
                if (out === 0) return null;
                const d = receiveDraft[line.id] ?? { qty: String(out), cost: line.unitCost };
                return (
                  <div key={line.id} className="rounded-xl border border-slate-200 p-3">
                    <div className="font-semibold text-slate-900">{line.itemName}</div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <label className="text-xs">
                        Qty to receive (max {out})
                        <input
                          type="number"
                          min="0"
                          max={out}
                          className="mt-1 w-full rounded border px-2 py-1"
                          value={d.qty}
                          onChange={(e) =>
                            setReceiveDraft((prev) => ({
                              ...prev,
                              [line.id]: { ...d, qty: e.target.value },
                            }))
                          }
                        />
                      </label>
                      <label className="text-xs">
                        Actual unit cost
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="mt-1 w-full rounded border px-2 py-1"
                          value={d.cost}
                          onChange={(e) =>
                            setReceiveDraft((prev) => ({
                              ...prev,
                              [line.id]: { ...d, cost: e.target.value },
                            }))
                          }
                        />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button type="button" className="rounded-xl border px-4 py-2 text-sm font-semibold" onClick={() => setReceiveOpen(false)}>
                Close
              </button>
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                disabled={receiveMut.isPending}
                onClick={() => receiveMut.mutate()}
              >
                Confirm receive
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <PrintInventoryLabelsModal
        open={printLabelsOpen}
        onClose={() => setPrintLabelsOpen(false)}
        title="Print labels for received stock"
        items={labelItems}
      />
    </div>
  );
}
